<template>
  <div :key="mainIndId" class="plotter" :layout="layout">
    <plotter-header v-if="!isThumbnail" :country-selection="countrySelection" :data-countries="dataCountries"
      :main-breakdown-selection="mainBreakdownSelection" :main-breakdown-dimensions="mainBreakdownDimensions"
      :pair-breakdown-selection="pairBreakdownSelection" :pair-breakdown-dimensions="pairBreakdownDimensions"
      :size-breakdown-selection="sizeBreakdownSelection" :size-breakdown-dimensions="sizeBreakdownDimensions"
      :flip-dimensions="flipDimensions" :units="{ main: mainUnits, pair: pairUnits, size: sizeUnits }"
      @update="updateSelection($event)" />
    <div class="plotter__outputs">
      <div v-if="loading" class="plotter__loader">
        <div class="text">Loading</div>
      </div>
      <template v-else>
        <template v-if="currentChartData.length > 1">
          <div class="chart-container">
            <chart :key="chartType" :selection="countrySelection" :dataset="currentChartData" :type="chartType"
              :time-scale="timeScale.id" :time-interval="chartTimeInterval" :is-highlight="isThumbnail"
              :units="{ x: mainUnit, y: pairUnit, z: sizeUnit }"
              :titles="{ x: mainTitle , y: pairTitle}"
              @update:selection="updateSelection({ slot: 'main', dim: 'countries', value: $event })" />
            <chart-controls v-if="isExploreLayout" :class="'plotter__controls-pannel'" :mode="'compare'"
              :chart-data="currentChartData" />
          </div>
        </template>
        <div v-else class="plotter__error">
          <div v-if="hasNoData" class="text no-data">
            No data to compare
          </div>
          <div v-else class="text no-selection">
            Select at least one indicator for <a @click="openSidebar('main')">X axis</a> and one for
            the <a @click="openSidebar('pair')">Y axis</a>
          </div>
        </div>
        <div v-if="!(isPrintable || isThumbnail)" class="plotter__time-selector">
          <time-scale-selector v-if="commonTimeScales.length > 1" v-model="timeScale" :selection="timeScale"
            :values="commonTimeScales" />
          <slider-selector v-if="timeSelection" v-model="currentDate" :data="timeSliderValues" :data-value="'value'"
            :data-label="'label'" :disabled="lastDates || dataTime.length <= 1" :hide-tooltip="isMobile || lastDates" />
          <div class="plotter__time-mode" :class="{ 'last-dates': lastDates }">
            <switcher-selector v-model="lastDates" />
            <label>Latest data available</label>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SwitcherSelector from '../selectors/SwitcherSelector.vue'
import SliderSelector from '../selectors/SliderSelector.vue'
import TimeScaleSelector from '../selectors/TimeScaleSelector.vue'

import PlotterHeader from './PlotterHeader.vue'
import Chart from '../Chart/Chart.vue'
import ChartControls from '../ChartControls/ChartControls.vue'

import ChartDataDriver from '../../mixins/ChartDataDriver.js'
import isFlipped from '../../mixins/isFlipped'

import { prune as pruneTree } from '../../utils/tree'

import PlotterData from './PlotterData.js'

export default {
  components: {
    SliderSelector,
    PlotterHeader,
    Chart,
    ChartControls,
    SwitcherSelector,
    TimeScaleSelector,
  },
  mixins: [ChartDataDriver, isFlipped],
  computed: {
    currentDate: {
      get() {
        return this.lastDates
          ? this.dataTime[this.dataTime.length - 1]
            ? this.dataTime[this.dataTime.length - 1].getTime()
            : this.timeSelection.getTime()
          : this.timeSelection.getTime()
      },
      set(to) {
        if (!this.lastDates) this.timeSelection = new Date(+to)
      },
    },
    chartTimeInterval() {
      return [this.timeSelection]
    },
    chartType() {
      return 'scatterplot'
    },
    hasNoData() {
      return !this.loading && this.$store.state.data.pair !== null
        ? this.currentChartData.length <= 1
        : false
    },
    currentChartData() {
      if (this.mainChartData.length < 2 || this.pairChartData.length < 2) {
        return []
      }

      const selectedCountries = Object.fromEntries(this.countrySelection.map(d => [d.id, true]))
      const countryFilter = this.countryFilter && Object.keys(selectedCountries).length > 0
      function isCountrySelected(iso) {
        return countryFilter ? (selectedCountries[iso] === true && 1) || 0 : true
      }

      const data = new PlotterData(
        {
          main: this.mainChartData,
          pair: this.pairChartData,
          size: this.sizeChartData || [],
        },
        {
          dates: {
            main: this.mainAvailableDates,
            pair: this.pairAvailableDates,
            size: this.sizeAvailableDates || [],
          },
          countries: {
            main: this.mainCountryDimension.options.map(d => d.id),
            pair: this.pairCountryDimension?.options.map(d => d.id),
            size: this.sizeCountryDimension?.options.map(d => d.id) || [],
          },
          powers: {
            main: this.power('main'),
            pair: this.power('pair'),
            size: this.power('size'),
          },
          lastDates: this.lastDates,
          timeScale: this.timeScale.id,
        }
      )

      const currentDate = data.dataset.dates[this.timeSelection.getTime()]
        ? this.timeSelection.getTime()
        : Object.keys(data.dataset.dates).sort((a, b) => a - b)[
        Object.keys(data.dataset.dates).length - 1
        ]

      data.select((row, fields) => {
        return (
          (this.lastDates || row[fields._DATE_].getTime() === +currentDate) &&
          isCountrySelected(row[fields._COUNTRY_])
        )
      })

      return data
    },
    dataCountries() {
      if (this.currentChartData.length === 0) return []
      const countryDimension = this.$store.getters.mainDimensions.find(d => d.type === 'country')
      return pruneTree(countryDimension.options, this.currentChartData.countries)
      /* return this.mainCountryDimension.options.filter( */
      /*   option => this.currentChartData.countries[option.id] */
      /* ) */
    },
    dataTime() {
      if (this.currentChartData.length === 0) return []
      return Object.keys(this.currentChartData.dataset.dates)
        .map(v => new Date(+v))
        .sort((a, b) => a - b)
    },
    commonTimeScales() {
      const mainScales = this.mainTimeScales.map(d => d)
      const pairScales = this.pairTimeScales.map(d => d.id)
      const sizeScales =
        this.$store.state.data.size !== null
          ? this.sizeTimeScales.map(d => d.id)
          : this.mainTimeScales.map(d => d.id)

      return mainScales.reduce((commons, scale) => {
        if (pairScales.includes(scale.id) && sizeScales.includes(scale.id)) {
          commons.push(scale)
        }
        return commons
      }, [])
    },
    mainTitle: {
      get() {
        if (!this.isExploreLayout || this.isPrintable) return

        let name
        try {
          name = this.$store.getters.indicatorName('main', this.mainIndId, this.lng)
        } catch {
          return
        }

        const dimensions = this.mainBreakdownDimensions.filter(d => !d.notApplicable)
          .filter(d => !this.isNDSC || d.id !== 'CL_GD_BREAKDOWNS_21_POLICY')

        const title = name + (this.mainUnit?.label ? ` (${this.mainUnit.label})` : '');
        return `<div class="axis-title axis-title--x">
          <div class="title">${title}</div>
          <div class="breakdowns">
            ${dimensions.map(d => `<div>
              <label><b>${d.name.en}:</b></label>
              <span>${this.mainBreakdownSelection[d.id].labels.en}</span>
            </div>`).join("")}
          </div>
        </div>`
      },
    },
    pairTitle: {
      get() {
        if (!this.isExploreLayout || !this.pairIndId || this.isPrintable) return

        let name
        try {
          name = this.$store.getters.indicatorName('pair', this.pairIndId, this.lng)
        } catch {
          return
        }

        const dimensions = this.pairBreakdownDimensions.filter(d => !d.notApplicable)
          .filter(d => !this.isNDSC || d.id !== 'CL_GD_BREAKDOWNS_21_POLICY')

        const title = name + (this.pairUnit?.label ? ` (${this.pairUnit.label})` : '')
        return `<div class="axis-title axis-title--y"> 
          <div class="title">${title}</div>
          <div class="breakdowns">
            ${dimensions.map(d => `<div>
              <label><b>${d.name.en}:</b></label>
              <span>${this.pairBreakdownSelection[d.id].labels.en}</span>
            </div>`).join("")}
          </div>
        </div>`
      },
    },
  },
  watch: {
    selectedCountries(to) {
      if (!to || Object.keys(to).length === 0) {
        if (this.$store.state.query.countryFilter) this.$store.commit('countryFilter', false)
      }
    },
    dataTime: {
      handler(to) {
        if (!this.timeSelection) return
        if (to.map(d => d.getTime()).indexOf(this.timeSelection.getTime()) === -1) {
          const lastAvailable = to[to.length - 1]
          if (lastAvailable) {
            this.timeSelection = lastAvailable
          }
        }
      },
      immediate: true,
    },
  },
  beforeMount() {
    if (this.pairCubeId && this.pairIndId && !this.$store.state.cubeMeta.pair) {
      const pairCubeId = this.pairCubeId
      const pairIndId = this.pairIndId
      const pairBreakdowns = Object.assign({}, this.pairBreakdowns)
      this.$store.commit('pairCubeId', null)
      this.$nextTick(() => {
        this.$store.commit('pairCubeId', pairCubeId)
        this.$store.commit('pairIndId', pairIndId)
        this.$store.commit('pairBreakdowns', pairBreakdowns)
      })
      console.warn('force pair meta update')
    }
    if (this.sizeCubeId && this.sizeIndId && !this.$store.state.cubeMeta.size) {
      const sizeCubeId = this.sizeCubeId
      const sizeIndId = this.sizeIndId
      const sizeBreakdowns = Object.assign({}, this.sizeBreakdowns)
      this.$store.commit('sizeCubeId', null)
      this.$nextTick(() => {
        this.$store.commit('sizeCubeId', sizeCubeId)
        this.$store.commit('sizeIndId', sizeIndId)
        this.$store.commit('sizeBreakdowns', sizeBreakdowns)
      })
      console.warn('force size meta update')
    }
  },
  methods: {
    openSidebar(slot) {
      this.$root.$emit('sidebar:open', slot)
    },
  },
}
</script>

<style lang="stylus">
.plotter
  display: flex
  flex: 1 1 0%
  flex-direction: column
  padding: 0 vw(75px) vw(20px)

  .mobile &
    padding: 0 vw(20px)

    +m-breakpoint(landscape)
      padding: 0 vw(20px) vh(20px)

  &[layout="embedded"]
  &[layout="print"]
  &[layout="thumbnail"]
    padding: 0 vw(50px)

  &[layout="embedded"]
    .mobile &
      padding: 0 vw(10px)

      +m-breakpoint(landscape)
        padding: 0 vw(10px) vh(30px)

.plotter__outputs
  display: flex
  flex: 1 1 0%
  flex-direction: column
  position: relative

  .mobile &
    min-height: 45vh

    +m-breakpoint(landscape)
      min-height: 80vh

  .plotter__error,
  .plotter__loader
    height: 100%
    left: 0
    position: absolute
    top: 0
    width: 100%

    .text
      left: 50%
      min-width: 50%
      position: absolute
      text-align: center
      top: 50%
      transform: translate(-50%, -50%)

      .mobile &
        +m-breakpoint(landscape)
          top: calc(50% - 15px)

      &.no-data
        font-size: 1.5rem
      > a
        color: #4283e3

  .plotter__loader
    background-image: url("../../assets/images/oecd-loader.gif")
    background-position: center
    background-repeat: no-repeat
    background-size: 100px

    .mobile &
      background-size: 100px

    .text
      padding-top: 180px
      font-size: 1.5rem
      left: 50%
      position: absolute
      top: 50%
      transform: translate(-50%, -50%)

      .mobile &
        padding-top: 140px

  .viz-root .cols-wrapper
    .axis-title
      .title
        font-size: 0.75em

      .breakdowns
        font-size: 0.6em
        display: flex
        gap: 1em

      &.axis-title--x
        margin: -1em -1em 1em 0
        text-align: right
        flex: 1

        .title
          text-align: right;

        .breakdowns
          justify-content: flex-end
          width: 100%
    
  .chart-container
    display: flex
    flex: 1 1 0%

    .mobile &
      flex-direction: column

  .chart
    flex: 1 1 50%

    .viz-root .tooltip .content .content-wrapper
       max-width: unset
       width: max-content

.plotter__controls-pannel
  position: absolute
  right: 0px
  top: 10px
  z-index: 10

.plotter__time-selector
  margin-top: auto
  align-items: center
  display: flex
  height: vh(90px)

  +m-breakpoint(sm md)
    height: unset

  .plotter__time-mode
    margin-left: vw(30px)

    .mobile &
      margin-left: vw(15px)
      display: flex

    label
      font-size: .7em

      .mobile &
        padding-left: vw(10px)
        font-size: .6em
        width: 65px
</style>
